import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Cropper from "react-easy-crop";
import axiosInstance from "../../utils/axios";
import uploadToCloudinary from "../../utils/cloudinary";
import toast from "react-hot-toast";
import { FaArrowLeft, FaSpinner, FaSave } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import { MdOutlineArrowForward } from "react-icons/md";
const BannerDesignerSkeleton = () => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 animate-pulse">
      {/* LEFT SIDE: Cropper & Controls */}
      <div className="lg:col-span-2 bg-white shadow-sm p-6 rounded-xl border space-y-6">
        {/* Instructions block */}
        <div className="h-24 bg-gray-200 rounded-lg" />

        {/* File picker */}
        <div className="flex items-center gap-3">
          <div className="h-10 w-32 bg-gray-200 rounded-md" />
          <div className="h-5 w-20 bg-gray-200 rounded-md" />
        </div>

        {/* Cropper skeleton */}
        <div className="w-full aspect-[2160/833] bg-gray-200 rounded-lg" />

        {/* Controls */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex-1">
            <div className="h-4 w-20 bg-gray-300 mb-2 rounded" />
            <div className="h-2 bg-gray-200 rounded-full w-full" />
          </div>
          <div className="h-10 w-20 bg-gray-200 rounded-md" />
          <div className="h-10 w-28 bg-gray-200 rounded-md" />
        </div>
      </div>

      {/* RIGHT SIDE: Live Preview + Form */}
      <div className="bg-white shadow-sm p-6 rounded-xl border space-y-5 h-fit sticky top-6">
        <div className="h-6 w-32 bg-gray-300 rounded" />

        {/* Preview skeleton */}
        <div className="w-full aspect-[2160/833] bg-gray-200 rounded-lg" />

        {/* Form skeleton */}
        <div className="space-y-4 mt-4">
          <div className="h-4 w-24 bg-gray-300 rounded" />
          <div className="h-10 bg-gray-200 rounded" />

          <div className="h-4 w-24 bg-gray-300 rounded" />
          <div className="h-10 bg-gray-200 rounded" />

          <div className="h-4 w-24 bg-gray-300 rounded" />
          <div className="h-10 bg-gray-200 rounded" />
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-3 mt-6">
          <div className="h-10 bg-gray-300 rounded-md" />
          <div className="h-10 bg-gray-200 rounded-md" />
        </div>
      </div>
    </div>
  );
};

/**
 * Helper: create a cropped image blob from react-easy-crop's croppedAreaPixels
 * Returns: { blob, url } where url is an object URL for preview (use URL.revokeObjectURL when done)
 */
async function getCroppedImg(
  imageSrc,
  pixelCrop,
  outputWidth = 2160,
  outputHeight = 833
) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext("2d");

  // draw the crop area from the source image onto the canvas with scaling
  const ratioX = image.naturalWidth / image.width;
  const ratioY = image.naturalHeight / image.height;

  // pixelCrop is relative to displayed image; scale to natural size
  const sx = pixelCrop.x * ratioX;
  const sy = pixelCrop.y * ratioY;
  const sWidth = pixelCrop.width * ratioX;
  const sHeight = pixelCrop.height * ratioY;

  // Draw with smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    sx,
    sy,
    sWidth,
    sHeight,
    0,
    0,
    outputWidth,
    outputHeight
  );

  // Convert to blob
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.9)
  );
  const url = URL.createObjectURL(blob);
  return { blob, url };
}

const cropAspect = 2160 / 833;

const AdminEditBanner = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== "undefined");

  const [form, setForm] = useState({
    title: "",
    sequence: 1,
    isActive: true,
    showGradient: true,
    image: "", // final cropped cloud URL
    originalImage: "", // uploaded original cloud URL
  });

  // cropper
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // previews + upload states
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState(null); // blob preview while uploading/loaded
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState(null); // preview from canvas before uploading (optional)
  const [uploadingOriginal, setUploadingOriginal] = useState(false);
  const [uploadingCropped, setUploadingCropped] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [cropChanged, setCropChanged] = useState(false);

  // refs
  const fileInputRef = useRef(null);
  const cropperContainerRef = useRef(null);

  // fetch existing banner for edit
  useEffect(() => {
    let cancelled = false;
    const fetchBanner = async () => {
      if (!isEdit) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const resp = await axiosInstance.get(`/banner/${id}`);
        const data = resp?.data?.data;
        if (!data) throw new Error("Banner not found");
        if (!cancelled) {
          setForm({
            title: data.title || "",
            sequence: data.sequence ?? 1,
            isActive: typeof data.isActive === "boolean" ? data.isActive : true,
            showGradient: typeof data.showGradient === "boolean" ? data.showGradient : true,
            image: data.image || "",
            originalImage: data.originalImage || data.image || "",
          });
          // ensure preview points at final cropped or original image
          setOriginalPreviewUrl(data.originalImage || data.image || null);
          setCroppedPreviewUrl(data.image || null);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load banner");
        navigate("/admin-banners");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBanner();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  // handle file selection (modern uploader)
  const handleChooseFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const blobUrl = URL.createObjectURL(file);
    setOriginalPreviewUrl(blobUrl);
    setUploadingOriginal(true);

    try {
      const uploadedUrl = await uploadToCloudinary(file);
      setForm((p) => ({ ...p, originalImage: uploadedUrl }));
      URL.revokeObjectURL(blobUrl);
      setOriginalPreviewUrl(uploadedUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      if (croppedPreviewUrl && croppedPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(croppedPreviewUrl);
        setCroppedPreviewUrl(null);
      }
    } catch (err) {
      console.error("Original upload failed", err);
      toast.error("Original upload failed");
      // keep the local preview so user can try again
    } finally {
      setUploadingOriginal(false);
      // clear file input for future picks
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  // called by react-easy-crop
  const onCropComplete = useCallback((area, pixels) => {
    setCroppedAreaPixels(pixels);
    setCropChanged(true);
  }, []);

  // generate final cropped image (canvas) and upload
  const handleGenerateCropAndUpload = async () => {
    if (!form.originalImage && !originalPreviewUrl) {
      return toast.error("Please upload an original image first");
    }
    // choose source for cropping: if originalImage is a cloud url use that, else use local preview URL
    const source = form.originalImage || originalPreviewUrl;
    if (!croppedAreaPixels) return toast.error("Adjust crop area first");

    try {
      setUploadingCropped(true);
      // create cropped blob + preview url
      const { blob, url } = await getCroppedImg(
        source,
        croppedAreaPixels,
        2160,
        833
      );
      // show immediate cropped preview (blob)
      if (croppedPreviewUrl && croppedPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(croppedPreviewUrl);
      }
      setCroppedPreviewUrl(url);

      // upload cropped blob to Cloudinary
      const uploadedUrl = await uploadToCloudinary(blob);
      setForm((p) => ({ ...p, image: uploadedUrl }));
      // replace preview with cloud url
      setCroppedPreviewUrl(uploadedUrl);
      setCropChanged(false);

      toast.success("Cropped upload successful");
    } catch (err) {
      console.error("Crop/upload error", err);
      toast.error("Failed to generate/upload cropped image");
    } finally {
      setUploadingCropped(false);
    }
  };

  // Save banner metadata to backend
  const handleSave = async () => {
    if (!form.image)
      return toast.error(
        "Please generate and upload cropped banner before saving"
      );

    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        sequence: Number(form.sequence) || 1,
        isActive: !!form.isActive,
        showGradient: !!form.showGradient,
        image: form.image,
        originalImage: form.originalImage || form.image,
      };

      if (isEdit) {
        await axiosInstance.put(`/banner/${id}`, payload);
        toast.success("Banner updated");
      } else {
        await axiosInstance.post(`/banner`, payload);
        toast.success("Banner created");
      }
      navigate("/admin-banners");
    } catch (err) {
      console.error("Save error", err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // cleanup object URLs when leaving/unmounting
  useEffect(() => {
    return () => {
      if (originalPreviewUrl && originalPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(originalPreviewUrl);
      }
      if (croppedPreviewUrl && croppedPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(croppedPreviewUrl);
      }
    };
  }, [originalPreviewUrl, croppedPreviewUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center gap-3 px-6 py-4 bg-white shadow-sm">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded"
          >
            <FaArrowLeft className="text-gray-700" />
          </button>
          <h1 className="text-xl font-semibold text-gray-700">
            {isEdit ? "Edit Banner" : "Create Banner"}
          </h1>
        </div>

        <div className="max-w-full mx-auto px-4 py-4">
          <BannerDesignerSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* header */}
      <div className="flex items-center gap-3 px-6 py-4 bg-white shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded"
        >
          <FaArrowLeft className="text-gray-700" />
        </button>
        <h1 className="text-xl font-semibold text-gray-700">
          {isEdit ? "Edit Banner " : "Create Banner "}
        </h1>
      </div>

      <div className="max-w-full mx-auto px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Designer / Cropper */}
          <div className="lg:col-span-2 bg-white shadow-sm p-6 rounded-xl border">
            {/* Designer Instructions */}
            <div className=" px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-1">
                How to set the banner image
              </h3>
              <ul className="text-xs text-blue-700 space-y-1 leading-relaxed">
                <li>
                  • Choose a high-resolution image (preferred ratio: 2160 ×
                  833).
                </li>
                <li>
                  • Drag the image to adjust the visible area inside the banner
                  frame.
                </li>
                <li>
                  • Use the zoom slider to fine-tune how much of the image is
                  visible.
                </li>
                <li>
                  • After positioning, click <strong>"Continue"</strong> to save
                  the final cropped image.
                </li>
                <li>
                  • The preview on the right updates instantly as you design.
                </li>
              </ul>
            </div>

            {/* modern file picker */}
            <div className="mt-3 flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleChooseFile}
                className="hidden"
                id="bannerFile"
              />
              <label
                htmlFor="bannerFile"
                className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                <span className="text-sm font-medium">Choose Image</span>
              </label>

              {uploadingOriginal && (
                <div className="inline-flex items-center gap-2 text-sm text-gray-600">
                  <FaSpinner className="animate-spin" />
                  Uploading...
                </div>
              )}

              {!uploadingOriginal &&
                (originalPreviewUrl || form.originalImage) && (
                  <div className="text-sm text-gray-600">Uploaded</div>
                )}
            </div>

            {/* Cropper */}
            <div className="mt-4">
              {form.originalImage || originalPreviewUrl ? (
                <div className="relative rounded-lg overflow-hidden border">
                  <div
                    className="w-full aspect-[2160/833] bg-black relative"
                    ref={cropperContainerRef}
                  >
                    <Cropper
                      image={form.originalImage || originalPreviewUrl}
                      crop={crop}
                      zoom={zoom}
                      aspect={cropAspect}
                      onCropChange={(c) => {
                        setCrop(c);
                        setCropChanged(true);
                      }}
                      onZoomChange={(z) => {
                        setZoom(z);
                        setCropChanged(true);
                      }}
                      onCropComplete={onCropComplete}
                      showGrid={true}
                      objectFit="horizontal-cover"
                    />
                    {/* left->right black gradient overlay (preview only) */}
                    {form.showGradient === true && (
                      <div className="absolute inset-0 pointer-events-none">
                        <div
                          style={{
                            background:
                              "linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0.0) 100%)",
                            height: "100%",
                            width: "100%",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full aspect-[2160/833] rounded-lg bg-gray-50 border-2 border-dashed border-gray-300 
                        flex  flex-col items-center justify-center text-gray-500 cursor-pointer 
                        hover:bg-gray-100 transition duration-200"
                >
                  <FiUpload className="text-4xl text-gray-400 mb-2" />

                  <p className="text-sm font-medium">
                    Click to upload an image
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Preferred size: 2160 × 833
                  </p>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                    setCroppedAreaPixels(null);
                    if (
                      croppedPreviewUrl &&
                      croppedPreviewUrl.startsWith("blob:")
                    ) {
                      URL.revokeObjectURL(croppedPreviewUrl);
                      setCroppedPreviewUrl(null);
                    }
                    toast.success("Reset");
                  }}
                  className="px-3 py-2 border rounded-md text-sm"
                >
                  Reset
                </button>

                <button
                  onClick={handleGenerateCropAndUpload}
                  disabled={uploadingCropped || (!cropChanged && form?.image)}
                  className={`
                            px-4 py-2 rounded-md flex items-center gap-2 transition-all duration-200
                            ${
                              uploadingCropped || (!cropChanged && form?.image)
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
                                : "bg-primaryColor text-white hover:bg-[#022b31]"
                            }
                          `}
                >
                  {uploadingCropped ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <MdOutlineArrowForward />
                  )}
                  Continue
                </button>
              </div>
            </div>
          </div>

          {/* Right: Live preview + form */}
          <div className="bg-white shadow-sm p-6 rounded-xl border h-fit sticky top-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Live Preview
            </h2>

            {/* Preview area */}
            <div className="w-full aspect-[2160/833] rounded-lg overflow-hidden border relative">
              {/* image preview: precedence -> croppedPreviewUrl (blob or cloud), form.image (cloud), originalPreviewUrl */}
              <img
                src={
                  croppedPreviewUrl ||
                  form.image ||
                  form.originalImage ||
                  originalPreviewUrl ||
                  ""
                }
                alt=""
                className="w-full h-full object-cover"
                draggable={false}
              />

              {/* black gradient overlay left->right */}
              {form.showGradient === true && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0.0) 100%)",
                  }}
                />
              )}

              {/* title overlay: left-aligned, vertically centered */}
              <div className="absolute inset-0 flex items-center">
                <div className="px-4 w-full">
                  <div 
                    className={`text-white text-xl font-bold ${!form.showGradient ? 'drop-shadow-lg' : ''}`}
                    title={form.title}
                  >
                    {form.title}
                  </div>
                </div>
              </div>
            </div>

            {/* Form fields */}
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                  className="mt-1 w-full border rounded px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Large title centered vertically on the banner.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Sequence
                </label>
                <input
                  type="number"
                  value={form.sequence}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, sequence: Number(e.target.value) }))
                  }
                  className="mt-1 w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  value={String(form.isActive)}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      isActive: e.target.value === "true",
                    }))
                  }
                  className="mt-1 w-full border rounded px-3 py-2 cursor-pointer"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              {/* Gradient Toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="showGradient"
                  checked={form.showGradient}
                  onChange={(e) => setForm(p => ({ ...p, showGradient: e.target.checked }))}
                  className="w-4 h-4 text-primaryColor rounded border-gray-300 focus:ring-primaryColor cursor-pointer"
                />
                <label htmlFor="showGradient" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Show Dark Gradient Overlay
                </label>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={handleSave}
                disabled={saving || cropChanged || !form?.image}
                className={`w-full px-4 py-2 rounded-md flex items-center justify-center gap-2 
                          transition-all duration-200
                          ${
                            saving || cropChanged || !form?.image
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
                              : "bg-primaryColor text-white hover:bg-[#022b31]"
                          }
                        `}
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isEdit ? "Save Changes" : "Create Banner"}
              </button>

              <button
                onClick={() => {
                  navigate("/admin-banners");
                }}
                className="w-full px-4 py-2 border rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEditBanner;
